import React, { useEffect, useMemo, useRef } from "react";
import { useMeCore } from "../../cores/me";
import { useIntl } from "react-intl";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRocket } from "@fortawesome/free-solid-svg-icons";
import Avatar from "../component/Avatar";
import { getFirstLetters } from "../utils/stringUtils";
import { HomeProductIntro } from "../component/home/HomeProductIntro";
import { beforeUploadFile, UploadFileType } from "../../cores/utils";
import { ME_CONFIG_RULES } from "../../cores/me/config";
import { useGlobalCore } from "../../cores/globals";
import { EDIT_ME_AVATAR_USER_LOADING } from "../../constants/loading.constant";
import { UpdateLoadingCard } from "../component/UpdateLoadingCard";

/** Pantalla de bienvenida (saludo); el panel con métricas y acciones está en HomeDashboard. */
const Home = () => {
  const intl = useIntl();
  const { user, company, getMeSocialSettings, getMeUserSettings, postMeAvatar } = useMeCore();
  const { isLoading } = useGlobalCore();
  const fileRef = useRef<HTMLInputElement | null>(null);

  const filesAllowed = useMemo(
    () => [UploadFileType.JPEG, UploadFileType.JPG, UploadFileType.PNG],
    []
  );

  const handleSelectPhoto = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const error = !beforeUploadFile(
      files[0],
      ME_CONFIG_RULES.maxImageSize,
      filesAllowed
    );
    if (!error) postMeAvatar(files[0]);
    // allow selecting same file again
    if (fileRef.current) fileRef.current.value = "";
  };

  const getGreet = () => {
    const date = new Date();
    const hour = date.getHours();
    if (hour >= 0 && hour <= 12) return `¡Buen día, ${user?.firstName}!`;
    if (hour >= 13 && hour <= 18) return `¡Buenas tardes, ${user?.firstName}!`;
    return `¡Buenas noches, ${user?.firstName}!`;
  };

  useEffect(() => {
    getMeUserSettings();

    if (company) getMeSocialSettings(company.id);
  }, [getMeSocialSettings, getMeUserSettings, company]);

  return (
    <div className="home-container home-container--welcome">
      <section className="hero-section">
        <div className="hero-content home-welcome-shell">
          <div className="user-welcome">
            <div className="d-flex flex-column align-items-center align-items-sm-start">
              <label
                className="pointer m-0 position-relative"
                htmlFor="homeAvatarUploader"
                title="Cambiar foto de perfil"
              >
                {!isLoading(EDIT_ME_AVATAR_USER_LOADING) && (
                  <Avatar
                    image={user?.avatarUrl}
                    height="110px"
                    width="110px"
                    label={getFirstLetters(`${user?.firstName} ${user?.lastName}`)}
                    className="hero-avatar"
                  />
                )}
                <UpdateLoadingCard
                  loadingId={EDIT_ME_AVATAR_USER_LOADING}
                  text={intl.formatMessage({ id: "app.navbar.updatingAvatar" })}
                />
              </label>
              <input
                ref={fileRef}
                type="file"
                id="homeAvatarUploader"
                className="d-none"
                accept={filesAllowed.join(",")}
                onChange={(e) => handleSelectPhoto(e.target.files)}
              />
            </div>
            <div className="welcome-text">
              <h1 className="display-5 fw-bold ">{getGreet()}</h1>
              <p className="lead mb-0 text-muted">
                <FontAwesomeIcon icon={faRocket} className="me-2" />
                {intl.formatMessage({ id: "app.home.subtitle" })}
              </p>
            </div>
          </div>
        </div>
      </section>
      <HomeProductIntro />
    </div>
  );
};

export default Home;
